import { type GraphqlQuery, type GraphqlResponse, type MimeTypeEnumExtended, ZnglyMimeTypes } from '@/Types';
import type { OembedV2_Query$data } from '@/graphql/queries/__generated__/OembedV2_Query.graphql';
import { graphqlEndpoint } from '@/graphql/relay-env'; // import sanHtml from 'sanitize-html';
import type { OembedV2 } from '@/graphql/types';
import DOMPurify from 'dompurify';

export const fetchGraphql = async <T>(data: GraphqlQuery): Promise<GraphqlResponse<T>> => {
  if (typeof data !== 'object') {
    throw new Error('Parameter is not an object!');
  }

  const response = await fetch(graphqlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(data),
  });

  return await response.json();
};

export const strToUpperCaseFirst = (str: string) => {
  if (str === '') return '';
  const tokens = str.split(' ');
  const updatedStrs = tokens.map((token) => {
    return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
  });
  return updatedStrs.join(' ');
};

export const sanitize = (str?: string | null) => {
  if (!str) return '';
  return DOMPurify.sanitize(str, {
    ALLOWED_TAGS: ['#text'],
    KEEP_CONTENT: true,
  });
};

// @todo~: fix bug where sanitization breaks center styling
export const sanitizeHtml = (html: string) => {
  if (!html) return '';
  if (html === '<h2 style="text-align:start;"></h2>\n') return '';

  return html;
};
// sanHtml(html, {
// 	allowedStyles: {
// 		'*': {
// 			// Match HEX and RGB
// 			color: [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/],
// 			'text-align': [/^left$/, /^right$/, /^center$/],
// 			// Match any number with px, em, or %
// 			'font-size': [/^\d+(?:px|em|%)$/],
// 		},
// 		p: {
// 			'font-size': [/^\d+rem$/],
// 			'text-align': [/^left$/, /^right$/, /^center$/],
// 		},
// 	},
// });

export const arrayEquals = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((v, i) => v === b[i]);

export const isObject = (value: unknown) => {
  return value instanceof Object && Object.getPrototypeOf(value) === Object.prototype;
};

/**
 * @param {String} HTML representing a single element
 * @return {Element}
 */
export function htmlToElement(html: string): ParentNode {
  const template = document.createElement('template');
  template.innerHTML = html.trim(); // Never return a text node of whitespace as the result
  return template.content.firstChild! as unknown as ParentNode;
}

export function getTextFromHtmlString(html: string | null | undefined): {
  text: string;
  childrenLength: number;
} {
  if (!html || html === '' || html?.length < 3) return { text: '', childrenLength: 0 };

  const dom = DOMPurify.sanitize(html, { RETURN_DOM: true });
  const textOnly = DOMPurify.sanitize(html, { ALLOWED_TAGS: ['#text'], KEEP_CONTENT: true });

  if (dom.childNodes.length > 1) {
    return { text: textOnly, childrenLength: dom.childNodes.length };
  }

  return { text: textOnly, childrenLength: dom.childNodes?.length };
}

/**
 * @param {String} HTML representing any number of sibling elements
 * @return {NodeList}
 */
export function htmlToElements(html: string): NodeListOf<ParentNode> {
  const template = document.createElement('template');
  template.setHTMLUnsafe(html);
  return template.content.childNodes as unknown as NodeListOf<ParentNode>;
}
// var rows = htmlToElements('<tr><td>foo</td></tr><tr><td>bar</td></tr>');

export const isLocalhost = (type: 'react-dev-server' = 'react-dev-server') => {
  // for react dev server - check localhost:3000
  switch (type) {
    case 'react-dev-server':
      if (
        type === 'react-dev-server' &&
        ['3000', '1306'].includes(window.location.port) &&
        window.location.hostname === 'localhost'
      )
        return true;

      return false;
  }
};

// is the content outside of its div
type OverFlown = { clientWidth: number; clientHeight: number; scrollWidth: number; scrollHeight: number };
export const isOverflown = ({ clientWidth, clientHeight, scrollWidth, scrollHeight }: OverFlown) => {
  return scrollHeight > clientHeight || scrollWidth > clientWidth;
};

export const getOembedV2 = async (url: string): Promise<OembedV2> =>
  (
    (await fetchGraphql({
      query: `
			query ($url: String) { 
				oembedV2(url: $url) {
					html,
				}
			}`,
      variables: { url },
    })) as { data: OembedV2_Query$data }
  )?.data?.oembedV2 as { html: string };

/**
 * Debounce function - delays invoking the function until after wait milliseconds have elapsed since the last time the debounced function was invoked.
 */
export const debounce = <T extends unknown[] = unknown[], R = void>(
  func: (...args: T) => R,
  wait: number,
  immediate = false
): ((...args: T) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null;

  return (...args: T) => {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout as ReturnType<typeof setTimeout>);

    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
};

// generate unique id
export const uuid = (): string => {
  return crypto.randomUUID();
};

export type CopyFnArgs = {
  node: HTMLElement;
};

export const copyToClipboard = async (args: CopyFnArgs) => {
  await copyNodeToClipboard(args);
};

/**
 * Copies the email template to clipboard
 * @param node The node to copy
 */
const copyNodeToClipboard = async ({ node }: CopyFnArgs): Promise<void> => {
  try {
    const htmlCode = node.innerHTML
      .trim()
      // replace this generated mjml code which breaks the copy clipboard api
      .replace(/<!-->/g, '');

    const blobInput = new Blob([htmlCode], { type: 'text/html' });
    const clipboardItem = new ClipboardItem({ 'text/html': blobInput });

    await navigator.clipboard.write([clipboardItem]);
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * Selects a html node and copies it to clipboard
 * @param id The id of the element to copy
 */
export const copyNodeToHTML = async ({ node }: CopyFnArgs): Promise<void> => {
  // copy node to clipboard
  const html = node.innerHTML;

  // format html, remove all newlines and replace with spaces
  const htmlFormatted = html.replace(/\n/g, ' ');

  try {
    // copy html to clipboard
    await navigator.clipboard.writeText(htmlFormatted);
    Promise.resolve();
  } catch (error) {
    Promise.reject(error);
  }
};

// function which uses intersection observer api to see if a node is in view
export const isInView = (node: HTMLElement, callback: VoidFunction): IntersectionObserver => {
  const visibleOffset = 75;

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        callback();
      }
    },
    {
      root: null,
      rootMargin: `${visibleOffset}% 0px ${visibleOffset}% 0px`,
    }
  );

  observer.observe(node);

  return observer;
};

export const updateArrayItemOrder = <T>(items: T[], startIndex: number, endIndex: number): T[] => {
  const result = [...items];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return [...result];
};

// checks if the link contains a valid mime extension based on the allowed mime types
export const linkMimeChecker = (linkExtValue: string | null, allowedMimeTypes: MimeTypeEnumExtended[]) => {
  let linkExt = linkExtValue;
  if (linkExt === 'UNDEFINED') return false;
  if (!linkExt) return true;
  linkExt = linkExt.toLowerCase();

  const linkMimeType = allowedMimeTypes.find((mimeType) => {
    if (mimeType === ZnglyMimeTypes.ZnglyWildCard) return true;
    if (mimeType === ZnglyMimeTypes.ZnglyImage) {
      // check our link for all supported image types
      switch (linkExt) {
        case 'image':
        case 'link':
          return true;

        default:
          break;
      }
    }

    const mimeExt = mimeType.toString().split('_')[1].toLowerCase();

    // quick fix for jpg/jpeg
    if (linkExt === 'jpg') linkExt = 'jpeg';

    return mimeExt === linkExt;
  });

  return linkMimeType || false;
};

// Pretty print time in mm:ss format
export const prettyTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);

  const secondsStr = Math.trunc(seconds % 60)
    .toString()
    .padStart(2, '0');
  const minutesStr = (minutes % 60).toString().padStart(2, '0');

  if (seconds > 3599) {
    const hourStr = Math.floor(seconds / 3600).toString();
    return `${hourStr}:${minutesStr}:${secondsStr}`;
  }
  return `${minutesStr}:${secondsStr}`;
};

export const loadScript = (src: string, onLoad?: () => void) => {
  // check if script is already loaded
  // const existingScript = document.querySelector(`script[src="${src}"]`);
  // if (existingScript) {
  //   onLoad?.();
  //   return;
  // }

  const script = document.createElement('script');
  script.setAttribute('nonce', window.znRuntime?.cspStrictValue || '');
  script.src = src;
  script.defer = true;
  script.type = 'text/javascript';
  script.onload = () => {
    onLoad?.();
  };

  // insert script before the first occurence of the script tag
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
};

/**
 * Escape RegExp special characters
 * https://github.com/sindresorhus/escape-string-regexp#readme
 */
export default function escapeStringRegexp(str: string) {
  if (typeof str !== 'string') {
    throw new TypeError('Expected a string');
  }

  // Escape characters with special meaning either inside or outside character sets.
  // Use a simple backslash escape when it’s always valid, and a `\xnn` escape when the simpler form would be disallowed by Unicode patterns’ stricter grammar.
  return str.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d');
}
